import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, Mail, User as UserIcon, Store, MapPin, Hash, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * No-results conversion card. Shown on /restaurants when a diner's query
 * returns zero matches. Lets them flag the restaurant they want — we save
 * the request, email partnerships@nofee, and surface a thank-you message.
 *
 * Props:
 *  - prefillRestaurantName? string — if the search term looks like a name,
 *      pre-populate the restaurant field so the diner doesn't retype it.
 *  - prefillZip? string — same idea for 5-digit numeric queries.
 *  - prefillCity? string — same idea for non-numeric, non-name queries.
 */
const RequestRestaurantCard = ({
  prefillRestaurantName = '',
  prefillZip = '',
  prefillCity = '',
}) => {
  const [form, setForm] = useState({
    customer_name: '',
    customer_email: '',
    restaurant_name: prefillRestaurantName,
    city: prefillCity,
    zip_code: prefillZip,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (key) => (e) => setForm((s) => ({ ...s, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim()) return toast.error('Please enter your name.');
    if (!/.+@.+\..+/.test(form.customer_email)) return toast.error('Please enter a valid email.');
    if (!form.restaurant_name.trim()) return toast.error('Please enter the restaurant name.');

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/restaurant-requests`, {
        customer_name: form.customer_name.trim(),
        customer_email: form.customer_email.trim(),
        restaurant_name: form.restaurant_name.trim(),
        city: form.city.trim() || null,
        zip_code: form.zip_code.trim() || null,
      });
      if (res.data?.ok) {
        setDone(true);
        toast.success('Request received — thank you!');
      } else {
        toast.error('Could not send your request. Please try again.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send your request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="restaurant-request-success"
        className="max-w-2xl mx-auto rounded-3xl border border-emerald-500/30 bg-gradient-to-br from-emerald-950/40 via-zinc-950 to-black p-10 lg:p-12 text-center shadow-[0_40px_90px_-30px_rgba(16,185,129,0.25)]"
      >
        <div className="w-16 h-16 rounded-2xl bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center mx-auto mb-6">
          <Check className="w-7 h-7 text-emerald-300" strokeWidth={2.4} />
        </div>
        <p className="text-[11px] uppercase tracking-[0.28em] text-emerald-300 font-bold mb-3">
          Request received
        </p>
        <h3 className="text-3xl sm:text-4xl font-black text-white tracking-tight leading-tight">
          Thank you.
        </h3>
        <p className="mt-4 text-white/70 max-w-md mx-auto leading-relaxed text-[15px]">
          We will contact this restaurant and notify you if they join NoFee.
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="restaurant-request-card"
      className="max-w-2xl mx-auto rounded-3xl border border-gold-500/30 bg-gradient-to-br from-zinc-950 to-black p-8 lg:p-10 shadow-[0_40px_90px_-30px_rgba(212,175,55,0.25)]"
    >
      <div className="text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-gold-500/40 bg-gold-500/[0.08] mb-5">
          <Sparkles className="w-3.5 h-3.5 text-gold-400" />
          <span className="text-[10.5px] uppercase tracking-[0.22em] text-gold-300 font-bold">
            Help us grow NoFee
          </span>
        </div>
        <h2 className="text-2xl sm:text-3xl font-black text-white tracking-tight uppercase leading-tight">
          Can&apos;t find your favorite restaurant?
        </h2>
        <p className="mt-3 text-white/65 max-w-md mx-auto leading-relaxed">
          Help us bring it to NoFee.
        </p>
      </div>

      <form onSubmit={submit} className="mt-8 space-y-3 max-w-md mx-auto">
        <Field
          Icon={UserIcon}
          placeholder="Your name"
          value={form.customer_name}
          onChange={update('customer_name')}
          testid="request-customer-name"
          required
        />
        <Field
          Icon={Mail}
          type="email"
          placeholder="Your email"
          value={form.customer_email}
          onChange={update('customer_email')}
          testid="request-customer-email"
          required
        />
        <Field
          Icon={Store}
          placeholder="Restaurant name"
          value={form.restaurant_name}
          onChange={update('restaurant_name')}
          testid="request-restaurant-name"
          required
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field
            Icon={MapPin}
            placeholder="City (optional)"
            value={form.city}
            onChange={update('city')}
            testid="request-city"
          />
          <Field
            Icon={Hash}
            placeholder="ZIP (optional)"
            value={form.zip_code}
            onChange={update('zip_code')}
            testid="request-zip"
            inputMode="numeric"
          />
        </div>

        <button
          type="submit"
          disabled={submitting}
          data-testid="request-submit-btn"
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-2xl bg-gradient-to-b from-amber-300 to-gold-500 hover:from-amber-200 hover:to-gold-400 text-black font-extrabold uppercase tracking-[0.08em] text-[14px] disabled:opacity-60 transition-all shadow-[0_15px_40px_-15px_rgba(212,175,55,0.55)]"
        >
          {submitting ? 'Sending…' : 'Request this restaurant'}
        </button>

        <p className="text-[11px] text-white/40 text-center pt-1">
          We&apos;ll reach out to the restaurant and email you when they join NoFee. No spam.
        </p>
      </form>
    </div>
  );
};

const Field = ({ Icon, testid, ...rest }) => (
  <div className="relative">
    <Icon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gold-400/70" />
    <input
      {...rest}
      data-testid={testid}
      className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-white/[0.04] border border-white/10 text-white placeholder-white/45 focus:outline-none focus:border-gold-500/60 transition-colors"
    />
  </div>
);

export default RequestRestaurantCard;
